import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, startWith } from 'rxjs';

import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { NativeBridgeService } from '@fcom/core/services/native-bridge/native-bridge.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { isFinnairNorraOrWetLease } from '@fcom/core/utils/utils';
import { cartData, cartDataSelector } from '@fcom/booking/store/selectors/cart.selector';
import { Breakpoint } from '@fcom/common/interfaces/breakpoint.interface';
import { selectActiveTab } from '@fcom/booking-widget/store/selectors/booking-widget.selector';
import { WidgetTab } from '@fcom/booking-widget/interfaces/flight-search.interface';

import { getTripTypeFromBounds, isAwardBooking } from '../utils/common-booking.utils';
import { isFlight } from '../utils/flight.utils';

const MOBILE_VIEW_BREAKPOINT: Breakpoint = 'tablet_down';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  configService = inject(ConfigService);
  nativeBridgeService = inject(NativeBridgeService);
  store$ = inject(Store);
  private readonly mediaQueryService = inject(MediaQueryService);

  readonly isEnabled: Signal<boolean> = signal<boolean>(false);
  readonly isOpen = signal<boolean>(false);
  readonly showDesktopCart = computed(
    () => this.isEnabled() && !this.mediaQueryService.isBreakpoint(MOBILE_VIEW_BREAKPOINT)()
  );
  readonly showModalCart = computed(
    () => this.isEnabled() && this.mediaQueryService.isBreakpoint(MOBILE_VIEW_BREAKPOINT)()
  );
  readonly isAllowedToShowCtaInModal = signal(true);

  private readonly isAward: Signal<boolean> = signal<boolean>(false);
  private readonly cart = this.store$.selectSignal(cartDataSelector);

  private readonly tripType: Signal<TripType | undefined> = computed(() => {
    const bounds = this.cart()?.bounds;
    return bounds?.length ? getTripTypeFromBounds(bounds) : undefined;
  });

  private readonly allBoundsDirect: Signal<boolean> = computed(() =>
    (this.cart()?.bounds ?? []).every((bound) => bound.flights === 1)
  );

  // Norra and wet leases ("Iberia for Finnair") count as Finnair, the same way the cart contents treat them.
  private readonly allFlightsOperatedByFinnairGroup: Signal<boolean> = computed(() =>
    (this.cart()?.bounds ?? [])
      .flatMap((bound) => (bound.itinerary ?? []).filter(isFlight))
      .every((flight) => isFinnairNorraOrWetLease(flight.operatingAirline?.name, flight.operatingAirline?.code))
  );

  constructor() {
    if (!this.configService.cfg.enableShoppingCart) {
      return;
    }

    this.isAward = toSignal(this.isAward$(), { requireSync: true });

    const transfersAllowed = this.configService.cfg.enableShoppingCartForTransfers;
    const nonFinnairFlightsAllowed = this.configService.cfg.enableShoppingCartForNonFinnairFlights;

    this.isEnabled = computed(
      () =>
        !this.isAward() &&
        this.tripType() !== TripType.MULTICITY &&
        (transfersAllowed || this.allBoundsDirect()) &&
        (nonFinnairFlightsAllowed || this.allFlightsOperatedByFinnairGroup()) &&
        !this.nativeBridgeService.isInsideNativeWebView
    );
  }

  setVisible(visible: boolean): void {
    this.isOpen.set(visible);
  }

  private isAward$(): Observable<boolean> {
    return combineLatest([
      this.store$.select(selectActiveTab).pipe(startWith(null)),
      this.store$.pipe(cartData(), startWith(null)),
    ]).pipe(map(([tab, cart]) => tab === WidgetTab.AWARD || (cart ? isAwardBooking(cart.prices.unpaid) : false)));
  }
}
